// Modal shown when an instructor ends a class. Lets them choose which
// attendees should receive certificates (e.g. a student paid but did not
// attend can be left unchecked).
import { useEffect, useMemo, useState } from 'react'
import { Award, Check, RotateCcw, AlertCircle, Users } from 'lucide-react'

import { Modal } from '@/ui/modal'
import { Button } from '@/ui/button'
import { useClassSessionStore } from '@/features/classes/stores'
import type { SessionAttendee, Student } from '@/features/classes/stores'

import { AttendeeStatusBadge } from './StatusBadges'

interface EndClassModalProps {
  sessionId: string
  open: boolean
  onClose: () => void
  onEnd: (attendeeIds: string[]) => Promise<void>
}

export function EndClassModal({ sessionId, open, onClose, onEnd }: EndClassModalProps) {
  const loadSessionAttendees = useClassSessionStore((s) => s.loadSessionAttendees)
  const rawAttendees = useClassSessionStore((s) => s.attendees)
  const rawStudents = useClassSessionStore((s) => s.students)

  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Load the attendee roster when the modal opens, then default-select paid
  // and completed attendees so the instructor only has to uncheck no-shows.
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set())
      setError(null)
      return
    }

    setFetching(true)
    setError(null)
    void (async () => {
      try {
        await loadSessionAttendees(sessionId)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load attendees.')
      } finally {
        setFetching(false)
      }
    })()
  }, [open, sessionId, loadSessionAttendees])

  const attendees = useMemo((): Array<SessionAttendee & { student: Student }> =>
    rawAttendees
      .filter((a) => a.sessionId === sessionId)
      .map((a) => ({ ...a, student: rawStudents.find((st) => st.id === a.studentId)! }))
      .filter((a) => a.student),
    [rawAttendees, rawStudents, sessionId]
  )

  // Pre-select every attendee by default. Closing a class now certificates all
  // students unless the instructor explicitly excludes no-shows.
  useEffect(() => {
    if (!open || attendees.length === 0) {return}
    setSelectedIds((prev) => {
      if (prev.size > 0) {return prev}
      return new Set(attendees.map((a) => a.id))
    })
  }, [open, attendees.length, attendees])

  const allSelected = attendees.length > 0 && attendees.every((a) => selectedIds.has(a.id))

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(attendees.map((a) => a.id)))
    }
  }

  async function handleEnd() {
    setLoading(true)
    setError(null)
    try {
      await onEnd(Array.from(selectedIds))
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end class.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} showCloseButton className="max-w-2xl">
      <h3 className="text-lg font-semibold text-navy flex items-center gap-2 mb-1">
        <Award className="w-5 h-5 text-primary" />
        End Class & Issue Certificates
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Select the students who attended and should receive a certificate.
        Uncheck anyone who paid but did not attend.
      </p>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {fetching ? (
        <div className="py-10 flex flex-col items-center gap-2 text-slate-400 text-sm">
          <RotateCcw className="w-5 h-5 animate-spin" />
          Loading attendees…
        </div>
      ) : attendees.length === 0 ? (
        <div className="py-10 text-center text-slate-400 text-sm">
          <Users className="w-8 h-8 mx-auto mb-2 text-slate-200" />
          No students have joined this class yet.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="border-b border-slate-150 bg-gray-50">
                  <th className="text-left px-3 py-3 font-medium text-slate-500 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={attendees.length === 0}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary disabled:opacity-50"
                      aria-label="Select all students"
                    />
                  </th>
                  <th className="text-left px-3 py-3 font-medium text-slate-500">Student</th>
                  <th className="text-left px-3 py-3 font-medium text-slate-500">Status</th>
                  <th className="text-left px-3 py-3 font-medium text-slate-500">Check-in</th>
                  <th className="text-left px-3 py-3 font-medium text-slate-500">Score</th>
                </tr>
              </thead>
              <tbody>
                {attendees.map((a) => {
                  const isSelected = selectedIds.has(a.id)
                  const name = `${a.student.firstName} ${a.student.lastName}`
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-slate-150"
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(a.id)}
                          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                          aria-label={`Select ${name} for certificate`}
                        />
                      </td>
                      <td className="px-3 py-3 font-medium text-navy">{name}</td>
                      <td className="px-3 py-3">
                        <AttendeeStatusBadge status={a.status} />
                      </td>
                      <td className="px-3 py-3">
                        {a.checkedInAt ? (
                          <span className="inline-flex items-center gap-1 text-green-600 font-medium text-xs">
                            <Check className="w-3.5 h-3.5" />
                            {new Date(a.checkedInAt).toLocaleDateString('en-CA')}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {a.quizScore !== null ? (
                          <span className="font-semibold text-navy">{a.quizScore}%</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
            <span>
              {selectedIds.size} of {attendees.length} selected
            </span>
            {selectedIds.size === 0 && (
              <span className="text-amber-600">
                No students selected — ending will close the class without certificates.
              </span>
            )}
          </div>
        </>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleEnd}
          disabled={loading || fetching || attendees.length === 0}
        >
          {loading ? (
            <>
              <RotateCcw className="w-4 h-4 animate-spin" />
              Ending…
            </>
          ) : (
            'End Class'
          )}
        </Button>
      </div>
    </Modal>
  )
}
